/**
 * Presidential statements, the acts they became, and what the tagged names did after. D77.
 *
 * 🔴 **Why this is not the News screen with a filter, measured rather than assumed.** Over three
 * exhausted UTC days (2,339 articles) the wire carried 57 headlines naming him and **22 that were
 * him announcing something** — a bare name match is under 40% precise, and the rest is Eric and
 * Barron Trump, the `TRUMPUSD` token and *"Trump Accounts"*. The API's gate is what makes this
 * readable; see `jmt_stocks/trump.py`.
 *
 * ⛔ **He does not publish stock picks and this screen must never imply he does.** Of those 22
 * statements, 8 tagged `SPY`, 3 `USO`, 2 `EWC` and exactly **one** named companies. So the unit of
 * a row is *a statement and the names it touched*, and the biggest number on it is what the price
 * did — not a rating, not a rank, and not a suggestion.
 *
 * ⛔ **The move is not attribution.** `pctSince` says the price moved after he spoke. It does not
 * say he moved it: a tariff remark and a Fed leak in the same hour are indistinguishable here, and
 * the column header says "since" rather than "impact" for exactly that reason.
 *
 * 🔴 **Every row shows its layer, because the layer is the trust.** A `federal_register` row is the
 * operative act and is a median of five days old; a `wire` row is a headline from minutes ago. A
 * reader who cannot tell which is which cannot weigh them, so the badge is never omitted and never
 * abbreviated to something ambiguous.
 */
import type { JSX } from 'react';
import { Link } from 'react-router-dom';

import { EmptyState } from '../../design/components/messages/EmptyState.jsx';
import { Num } from '../../design/components/data/Num.jsx';
import { TrumpBoard } from './TrumpBoard.jsx';
import type { TrumpWatchCounts, TrumpWatchRow } from './TrumpBoard.jsx';

export type TrumpLayer = 'truth' | 'wire' | 'whitehouse' | 'federal_register';

export type TrumpStatement = {
  id: number;
  externalId: string;
  layer: TrumpLayer | string;
  saidAt: string;
  publishedAt?: string | null;
  lagDays?: number | null;
  statement: string;
  policyClass: string;
  symbols: string[];
  symbol?: string | null;
  name?: string | null;
  url?: string | null;
  source?: string | null;
  roundup?: boolean;
  baselinePrice?: string | null;
  lastPrice?: string | null;
  lastPriceAt?: string | null;
  priceSource?: string | null;
  pctSince?: string | null;
  pairedStatement?: string | null;
  pairedLayer?: string | null;
  pairedSaidAt?: string | null;
  candidates?: TrumpCandidate[];
};

export type TrumpRate = {
  policyClass: string;
  total: number;
  priced: number;
  medianAbsPct?: string | null;
};

/**
 * How each layer is labelled and what it means.
 *
 * ⚠ **The word in the badge is the *authority*, not the vendor.** "Proclamation" tells a reader
 * this is law; "federal_register" tells them where it was scraped from, which is not the thing they
 * need while scanning. The vendor is in the title attribute for when it matters.
 */
const LAYERS: Record<string, { label: string; hint: string }> = {
  truth: {
    label: 'post',
    hint: 'His own post, from the trumpstruth.org archive. Seconds old. Not an official record.',
  },
  wire: {
    label: 'wire',
    hint: 'Benzinga transcribing him, often quoting a Truth Social post verbatim. Minutes old.',
  },
  whitehouse: {
    label: 'action',
    hint: 'whitehouse.gov presidential actions — the operative act, published the same day.',
  },
  federal_register: {
    label: 'proclamation',
    hint:
      'The Federal Register: the operative, authoritative text — and a median of 5 days after ' +
      'signing (max 12 measured), so it is a record rather than news.',
  },
};

function ago(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const secs = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 90) return `${Math.round(secs)}s ago`;
  if (secs < 5400) return `${Math.round(secs / 60)}m ago`;
  if (secs < 172_800) return `${Math.round(secs / 3600)}h ago`;
  return `${Math.round(secs / 86_400)}d ago`;
}

/** Whether a price reading is old enough that the percentage beside it wants reading with care. */
function stalePrice(iso: string | null | undefined): boolean {
  if (!iso) return true;
  return Date.now() - new Date(iso).getTime() > 5 * 60_000;
}

export type TrumpCandidate = {
  ticker: string;
  company: string;
  cik?: string | null;
  isin?: string | null;
  theme: string;
  phrase?: string | null;
  exposureHits?: number | null;
  newestFiling?: string | null;
  exposed?: boolean | null;
  wirePosition: number;
  exposureRank?: number | null;
  totalCandidates: number;
  verdict?: string | null;
  verdictReason?: string | null;
  verdictOn?: string | null;
  marketCap?: number | null;
  screenerOn?: string | null;
};

/** `$1.3bn`, or `—`. Compact because the column is narrow and the magnitude is the point. */
function cap(value: number | null | undefined): string {
  if (value == null || value <= 0) return '—';
  if (value >= 1e12) return `$${(value / 1e12).toFixed(1)}tn`;
  if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}bn`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(0)}m`;
  return `$${value.toFixed(0)}`;
}

/**
 * The candidates a statement named, ranked by evidence of exposure.
 *
 * 🔴 **The header says "most exposed", not "best".** The ranking measures how much of its own
 * annual report a company spends on the theme, so it favours a single-line micro-cap over a
 * diversified major — measured: AeroVironment, the largest pure-play drone company, ranks fourth
 * behind Draganfly. A column headed "best" would point the reader at the smallest name every time.
 *
 * ⚠ **`exposed == null` is not `false`.** Null means the measurement has not run for that company
 * (SEC did not know the ticker, or the request failed and was left rather than written as zero).
 * The three states render differently on purpose.
 */
export function CandidateTable({ items }: { items: TrumpCandidate[] }): JSX.Element | null {
  if (items.length === 0) return null;
  const total = items[0]?.totalCandidates ?? items.length;
  const theme = items[0]?.theme ?? '';
  return (
    <div className="jmt-stk-tw__cands">
      <div className="jmt-stk-tw__cands-head">
        <span className="jmt-stk-tw__cands-title">
          most exposed to <b>{theme.replace(/_/g, ' ')}</b>
        </span>
        {/* ⚠ The truncation is stated. A 24-name basket shown as 12 rows with nothing saying so
            reads as "these are all of them". */}
        <span className="jmt-stk-tw__cands-note">
          {items.length < total ? `${items.length} of ${total}` : `${total}`} named
          {' · '}ranked by filings, not by size
        </span>
      </div>
      <table className="jmt-stk-tw__cands-table">
        <thead>
          <tr>
            <th scope="col">#</th>
            <th scope="col">company</th>
            <th scope="col" className="jmt-stk-tw__num">filings</th>
            <th scope="col">newest</th>
            <th scope="col" className="jmt-stk-tw__num">cap</th>
            <th scope="col">screener</th>
          </tr>
        </thead>
        <tbody>
          {items.map((c) => (
            <tr key={`${c.ticker}-${c.theme}`} className={c.exposed === false ? 'jmt-stk-tw__cand--none' : undefined}>
              <td className="jmt-stk-tw__num">{c.exposureRank ?? '—'}</td>
              <td>
                {/* The dossier link exists only when the universe knows the ticker. 35 of the 247
                    named tickers have no row, and a dead link is worse than plain text. */}
                {c.isin ? (
                  <Link to={`/screener/${c.isin}`} className="jmt-stk-tw__cand-name">
                    {c.company}
                  </Link>
                ) : (
                  <span className="jmt-stk-tw__cand-name">{c.company}</span>
                )}
                <code className="jmt-stk-tw__cand-tic">{c.ticker}</code>
                {/* ⚠ The publisher's own order, shown only when the ranking disagrees with it —
                    which is exactly when it is informative. */}
                {c.exposureRank != null && c.wirePosition !== c.exposureRank ? (
                  <span
                    className="jmt-stk-tw__cand-wire"
                    title={`The wire listed this name ${c.wirePosition} of ${total}; the filing evidence puts it ${c.exposureRank}.`}
                  >
                    wire #{c.wirePosition}
                  </span>
                ) : null}
              </td>
              <td className="jmt-stk-tw__num" title={c.phrase ? `searched for ${c.phrase}` : undefined}>
                {c.exposureHits == null ? (
                  <span className="jmt-stk-news__unknown" title="Not measured — SEC did not answer for this company, which is different from finding nothing.">
                    ?
                  </span>
                ) : (
                  c.exposureHits
                )}
              </td>
              <td>
                {c.newestFiling ? (
                  <span className={c.exposed === false ? 'jmt-stk-news__stale' : undefined}>
                    {c.newestFiling.slice(0, 7)}
                  </span>
                ) : (
                  <span className="jmt-stk-news__unknown">—</span>
                )}
              </td>
              {/* 🔴 Almost always blank, and that is honest rather than broken: a validated market
                  cap exists only for names the screener has published — measured 1 in 16. The
                  column stays because the ranking is purity, so a reader needs to see when the
                  size is unknown rather than assume it is small. */}
              <td className="jmt-stk-tw__num" title={c.screenerOn ? `from the screen run of ${c.screenerOn}` : 'no screener run has published this name, so no validated market cap exists'}>
                {cap(c.marketCap)}
              </td>
              <td>
                {c.verdict ? (
                  <span
                    className={`jmt-stk-tw__verdict jmt-stk-tw__verdict--${c.verdict.toLowerCase()}`}
                    title={c.verdictReason ?? undefined}
                  >
                    {c.verdict}
                  </span>
                ) : (
                  <span className="jmt-stk-news__unknown" title="The screener has never published this name. Absence is the common case, not a fault.">
                    —
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export type TrumpFilter = string | null;

export function TrumpWatchScreen({
  items,
  rates = [],
  filter = null,
  onFilter,
  companies = [],
  counts = null,
}: {
  items: TrumpStatement[];
  rates?: TrumpRate[];
  filter?: TrumpFilter;
  onFilter?: (next: TrumpFilter) => void;
  /** D81: the company-first board that now leads the page. */
  companies?: TrumpWatchRow[];
  counts?: TrumpWatchCounts | null;
}): JSX.Element {
  return (
    <div className="jmt-screen">
      {/* 🔴 **The board leads and the statement log follows.** D81 inverted this page: a reader
          arrives asking "is there anything here I should look at", which is a question about
          COMPANIES, and the statement feed answers a question about statements. The feed is still
          the record — every claim on the board is traceable to a row in it — so it stays, below,
          labelled as the log rather than as the page. */}
      <TrumpBoard rows={companies} counts={counts} />

      <h2 className="jmt-stk-tw__loghead">The statement log</h2>
      {/* 🔴 The standing disclaimer, on the screen rather than in a tooltip. This page pairs a
          politician's words with a price, which is the single most misreadable thing the platform
          shows; the sentence that says it is not advice has to be where the reader already is. */}
      <p className="jmt-stk-tw__caveat">
        What he said, and what the tagged names did afterwards. <b>Not a recommendation and not
        attribution</b> — the price moved after the statement, which is not the same as because of
        it. No rule reads this page.
      </p>

      {rates.length > 0 && onFilter ? (
        <div className="jmt-stk-tabs jmt-stk-tabs--wrap" role="tablist" aria-label="Filter by policy area">
          <button
            type="button"
            role="tab"
            aria-selected={filter === null}
            className={`jmt-stk-tab${filter === null ? ' jmt-stk-tab--on' : ''}`}
            onClick={() => onFilter(null)}
          >
            All
          </button>
          {rates.map((rate) => (
            <button
              key={rate.policyClass}
              type="button"
              role="tab"
              aria-selected={filter === rate.policyClass}
              /* ⚠ The tooltip carries `priced` beside `total` because they are usually not equal:
                 a statement with no symbol can never be priced, and 6 of 22 measured had none.
                 A median presented without its denominator overstates what it rests on. */
              title={
                `${rate.total} statements in the last 90 days, ${rate.priced} of them priced` +
                (rate.medianAbsPct == null
                  ? ' — no median, nothing measurable yet'
                  : `; median absolute move ${rate.medianAbsPct}%`)
              }
              className={`jmt-stk-tab${filter === rate.policyClass ? ' jmt-stk-tab--on' : ''}`}
              onClick={() => onFilter(rate.policyClass)}
            >
              {rate.policyClass} <span className="jmt-stk-tw__count">{rate.total}</span>
            </button>
          ))}
        </div>
      ) : null}

      {items.length === 0 ? (
        <div className="jmt-note">
          <EmptyState>
            {filter
              ? `Nothing filed under ${filter} in this window.`
              : 'No statements collected in this window. The collector runs on a timer; nothing to ' +
                'show is different from nothing running — measured, he averages about four ' +
                'market-relevant announcements a trading day.'}
          </EmptyState>
        </div>
      ) : (
        <div className="jmt-stk-news">
          {items.map((item) => {
            const layer = LAYERS[item.layer] ?? { label: item.layer, hint: '' };
            return (
              <article
                className={`jmt-stk-news__row${item.roundup ? ' jmt-stk-tw__row--wrap' : ''}`}
                key={item.id}
              >
                <div className="jmt-stk-tw__grid">
                  <div className="jmt-stk-tw__main">
                  <div className="jmt-stk-news__who">
                    <span className="jmt-stk-tw__layer" title={layer.hint}>
                      {layer.label}
                    </span>
                    <span className="jmt-stk-news__class">{item.policyClass}</span>
                    {/* ⚠ Shown only when it is not the whole story: a `wire` row is always 0 days
                        old and printing "0d" on every one of them trains the eye to skip the
                        column that matters on a proclamation. */}
                    {item.lagDays != null && item.lagDays > 0 ? (
                      <span
                        className="jmt-stk-tw__lag"
                        title="Signed this many days before it was published — the act existed before this record of it did."
                      >
                        +{item.lagDays}d
                      </span>
                    ) : null}
                    {/* ⚠ `null` lag means NOT KNOWABLE, never zero. Said in words rather than
                        shown as a dash, because a dash in a numeric column reads as zero. */}
                    {item.lagDays == null && (item.layer === 'federal_register') ? (
                      <span
                        className="jmt-stk-tw__lag jmt-stk-news__unknown"
                        title="This document carries no signing date, so how long it took to publish is unknown — not zero."
                      >
                        lag unknown
                      </span>
                    ) : null}
                    {item.roundup ? (
                      <span
                        className="jmt-stk-tw__wrap"
                        title="Reported inside a market summary rather than carried on its own. Kept because twice in the measured window the summary was the only row carrying the statement."
                      >
                        in round-up
                      </span>
                    ) : null}
                    </div>

                    <p className="jmt-stk-news__headline">
                      {item.url ? (
                        <a href={item.url} target="_blank" rel="noreferrer noopener">
                          {item.statement}
                        </a>
                      ) : (
                        item.statement
                      )}
                    </p>

                    {/* 🔴 The pairing: the operative act this remark became. The whole argument
                        for four layers rather than one — on 2026-08-19 the wire carried "I have
                        paused the 50% Tariffs against Canada" and whitehouse.gov published the
                        suspension proclamation the same day.

                        ⚠ Deliberately says which came first rather than assuming. Measured: the
                        proclamation was published four and a half hours BEFORE the post. */}
                    {item.pairedStatement ? (
                      <p className="jmt-stk-tw__pair">
                        <span className="jmt-stk-tw__pair-tag">
                          {LAYERS[item.pairedLayer ?? '']?.label ?? item.pairedLayer}
                        </span>
                        {item.pairedSaidAt && new Date(item.pairedSaidAt) < new Date(item.saidAt)
                          ? ' published before this — '
                          : ' followed — '}
                        {item.pairedStatement}
                      </p>
                    ) : null}

                    {item.candidates && item.candidates.length > 0 ? (
                      <CandidateTable items={item.candidates} />
                    ) : null}

                    <div className="jmt-stk-news__meta">
                      {item.symbols.length === 0 ? (
                        <span title="The publisher tagged no symbol. Most of his statements are macro, so this is the common case rather than a gap.">
                          no symbols tagged
                        </span>
                      ) : (
                        <span>
                          {/* ⚠ Every tagged symbol, not just the priced one. Breadth is the SIGNAL
                              on this page — a tariff statement legitimately touches every steel
                              name at once — the opposite of what it means on the News feed. */}
                          {item.symbols.join(' · ')}
                        </span>
                      )}
                      {item.name ? <span>{item.name}</span> : null}
                      {item.baselinePrice && item.lastPrice ? (
                        <span title={`Priced on ${item.symbol}`}>
                          {item.baselinePrice} → {item.lastPrice}
                        </span>
                      ) : null}
                      {item.source ? <span>{item.source}</span> : null}
                    </div>
                  </div>

                  {/* 🔴 The move since he spoke — the number the screen exists for, and the only
                      element on the row carrying direction colour. `Num` owns the sign, arrow and
                      ramp; the third colour law keeps green and red for P&L-shaped figures only.

                      ⚠ **`Number()`, not the string.** `formatNum` returns a string value verbatim
                      and `Num` gates the sign, the colour AND the arrow on `typeof value ===
                      'number'` — so passing the API's `"2.75"` straight through renders a bare
                      grey `2.75` with no percent sign and no direction. Caught by screenshot, not
                      by tsc: the prop is typed `string | null` upstream and every check was green.
                      */}
                  <div className="jmt-stk-news__figure">
                    {item.pctSince == null ? (
                      <span
                        className="jmt-stk-news__unknown"
                        title={
                          item.symbols.length === 0
                            ? 'This statement named no tradable symbol, so there is nothing to price. Most macro remarks are like this.'
                            : 'No baseline price captured for this statement yet.'
                        }
                      >
                        —
                      </span>
                    ) : (
                      <Num
                        value={Number(item.pctSince)}
                        kind="percent"
                        dp={1}
                        arrow
                        step="display"
                      />
                    )}
                    <div className="jmt-stk-news__stamps">
                      {/* ⚠ Two ages answering two questions: how old the STATEMENT is, and how
                          old the PRICE behind that percentage is. When the second is much older
                          than the first, the market has not traded since he spoke — which is why
                          the number can honestly read 0.0%. */}
                      <span title={`Said at ${item.saidAt}`}>{ago(item.saidAt)}</span>
                      {item.pctSince != null ? (
                        <span
                          className={
                            stalePrice(item.lastPriceAt) ? 'jmt-stk-news__stale' : undefined
                          }
                          title="When the price behind this percentage was last read"
                        >
                          price {ago(item.lastPriceAt)}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
